import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ArrowLeft, ChevronDown, Database, Home, LogOut, Settings, Sparkles, Users } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthProvider";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTabs } from "@/pages/admin/TabsContext";
import { WorkTimer } from "@/pages/admin/components/WorkTimer";

export function AdminHeader({
  backTo,
  title,
  backAs = "back",
}: {
  backTo?: string;
  title: string;
  backAs?: "back" | "home";
}) {
  const { logout, me } = useAuth();
  const { tabs, currentTab, setCurrentTabId } = useTabs();
  const nav = useNavigate();
  const location = useLocation();
  const isSuperAdmin = me?.role === "super_admin";
  // "Home" variant always jumps straight to `backTo` – Prev/Next has usually
  // stacked several ticket entries onto history, so nav(-1) would only step
  // one ticket back. "Back" variant keeps history semantics so pages like the
  // users/tabs editors restore the caller's filters/page transparently.
  const canGoBack = location.key !== "default";
  const goBack = () => {
    if (backAs === "home") {
      if (backTo) nav(backTo);
      return;
    }
    if (canGoBack) {
      nav(-1);
    } else if (backTo) {
      nav(backTo);
    }
  };
  return (
    <header className="sticky top-0 z-20 backdrop-blur bg-canvas/80 dark:bg-surface/80 border-b border-canvas-border dark:border-surface-border">
      <div className="w-full flex items-center gap-4 px-4 sm:px-6 lg:px-10 xl:px-14 py-3">
        <div className="flex-1 min-w-[14rem] flex items-center gap-2">
          {backTo && (
            <button
              type="button"
              onClick={goBack}
              className="btn-secondary text-xs px-3 py-1.5"
            >
              {backAs === "home" ? (
                <>
                  <Home size={14} /> Home
                </>
              ) : (
                <>
                  <ArrowLeft size={14} /> Back
                </>
              )}
            </button>
          )}
          {tabs.length > 0 && (
            <TabPicker
              tabs={tabs}
              currentId={currentTab?.id ?? null}
              onSelect={setCurrentTabId}
            />
          )}
          {me && <WorkTimer />}
        </div>
        <div className="shrink-0 text-center px-3">
          <Link to="/admin" className="text-sm sm:text-base font-semibold tracking-tight">
            {title}
          </Link>
        </div>
        <div className="flex-1 min-w-[14rem] flex items-center justify-end gap-2">
          <ThemeToggle />
          {isSuperAdmin && (
            <Link
              to="/admin/tabs"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-border focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              aria-label="Manage tabs"
              title="Manage tabs"
            >
              <Database size={12} /> Tabs
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              to="/admin/settings/prompt"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-border focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              aria-label="Edit the AI prompt"
              title="Edit the AI prompt"
            >
              <Sparkles size={12} /> Prompt
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-border focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              aria-label="Manage admin users"
              title="Manage admin users"
            >
              <Users size={12} /> Users
            </Link>
          )}
          {me && (
            <button
              type="button"
              onClick={() => void logout("manual")}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-border focus:outline-none focus:ring-2 focus:ring-brand-green/40"
              aria-label="Log out"
              title={`${me.username} (${me.role === "super_admin" ? "Super admin" : "Admin"})`}
            >
              <LogOut size={12} /> {me.username}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function TabPicker({
  tabs,
  currentId,
  onSelect,
}: {
  tabs: { id: string; name: string; read_only: boolean; schema_valid: boolean }[];
  currentId: string | null;
  onSelect: (id: string) => void;
}) {
  const current = tabs.find((t) => t.id === currentId);
  return (
    <Menu as="div" className="relative">
      <MenuButton className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-surface-border bg-white dark:bg-surface-raised px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-surface-border focus:outline-none focus:ring-2 focus:ring-brand-green/40">
        <Database size={12} />
        <span className="max-w-[10rem] truncate">{current?.name ?? "Select tab"}</span>
        <ChevronDown size={12} />
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="mt-1 w-64 rounded-md border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-raised shadow-lg focus:outline-none z-30"
      >
        {tabs.map((t) => (
          <MenuItem key={t.id}>
            {({ focus }) => (
              <button
                type="button"
                onClick={() => onSelect(t.id)}
                className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 ${
                  focus ? "bg-slate-50 dark:bg-surface-border" : ""
                } ${t.id === currentId ? "font-semibold" : ""}`}
              >
                <span className="truncate">{t.name}</span>
                <span className="flex gap-1">
                  {!t.schema_valid && (
                    <span
                      className="inline-flex items-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 text-[10px]"
                      title="Schema not validated"
                    >
                      <Settings size={9} />
                    </span>
                  )}
                  {t.read_only && (
                    <span
                      className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 text-[10px]"
                      title="Read-only"
                    >
                      RO
                    </span>
                  )}
                </span>
              </button>
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
